import type { Theme } from '@mui/material';

import { Iconify } from '../../components/iconify';
import { Box, Avatar, Skeleton, Stack } from '@mui/material';

import { imageOutlineSx } from '../../theme/surfaces';
import { getConnectorIcon, getConnectorBgColor, getConnectorAssetUrl } from '../types';

import type { ConnectorRecord } from '../types';

// microsoft_outlook_calendar ships its own baked-in rounded background (see
// libs/ari-lib/src/assets/icons/outlook-calendar.svg), so it renders at full tile
// size instead of inset like the other asset icons.
const FULL_TILE_ASSET_CONNECTOR_IDS = new Set(['microsoft_outlook_calendar']);

export function ConnectorIcon({ connector }: { connector: Pick<ConnectorRecord, 'id' | 'iconify_logo'> }) {
  const assetUrl = getConnectorAssetUrl(connector.id);
  const assetSize = FULL_TILE_ASSET_CONNECTOR_IDS.has(connector.id) ? 24 : 18;
  return (
    <Avatar
      variant="rounded"
      sx={(theme) => ({
        width: 24,
        height: 24,
        borderRadius: 0.5,
        bgcolor: getConnectorBgColor(connector.id, theme.palette.container.high),
        flexShrink: 0,
        ...imageOutlineSx(theme),
      })}
    >
      {assetUrl ? (
        <Box component="img" src={assetUrl} alt="" sx={{ width: assetSize, height: assetSize, objectFit: 'contain' }} />
      ) : (
        <Iconify icon={getConnectorIcon(connector)} width={16} />
      )}
    </Avatar>
  );
}

export function ConnectorRowSkeleton() {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        height: 42,
        px: 1,
        borderRadius: 1,
        border: `1px solid ${theme.palette.grey[700]}`,
      })}
    >
      <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
      <Skeleton variant="text" width={120} height={20} sx={{ flex: 0 }} />
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: 1 }} />
    </Box>
  );
}

export function ConnectorsSkeleton() {
  return (
    <Stack spacing={1.5}>
      <Box>
        <Skeleton variant="text" width={70} height={16} sx={{ mb: 1 }} />
        <Stack spacing={1}>
          {Array.from({ length: 2 }).map((_, i) => (
            <ConnectorRowSkeleton key={`c-${i}`} />
          ))}
        </Stack>
      </Box>
      <Box>
        <Skeleton variant="text" width={60} height={16} sx={{ mb: 1 }} />
        <Stack spacing={1}>
          {Array.from({ length: 4 }).map((_, i) => (
            <ConnectorRowSkeleton key={`f-${i}`} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

export const connectorRowSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  height: { xs: 48, md: 42 },
  px: 1,
  borderRadius: 1,
  border: `1px solid ${theme.palette.grey[700]}`,
  overflow: 'hidden',
});
